/**
 * ConfirmModal - Reusable confirmation dialog
 * Replaces native window.confirm() with styled modal
 */

import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { X, AlertTriangle, Trash2, Info, Loader2 } from 'lucide-react';
import '../AIGarage.css';

const ConfirmModal = ({
  isOpen,
  title = 'Confirm Action',
  message = 'Are you sure you want to proceed?',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  loadingText,
  isLoading = false,
  onConfirm,
  onCancel,
  variant = 'default' // 'default', 'danger', 'warning'
}) => {
  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = 'unset';
      };
    }
  }, [isOpen]);

  // Handle ESC key to close modal
  useEffect(() => {
    if (!isOpen || isLoading) return;

    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onCancel();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, isLoading, onCancel]);

  // Don't render if modal is not open
  if (!isOpen) return null;

  const getIcon = () => {
    switch (variant) {
      case 'danger':
        return <Trash2 size={24} color="#DC2626" />;
      case 'warning':
        return <AlertTriangle size={24} color="#F59E0B" />;
      default:
        return <Info size={24} color="#0684BC" />;
    }
  };

  const getConfirmButtonClass = () => {
    switch (variant) {
      case 'danger':
        return 'garage-btn garage-btn-danger';
      case 'warning':
        return 'garage-btn garage-btn-warning';
      default:
        return 'garage-btn garage-btn-primary';
    }
  };

  const dismiss = isLoading ? undefined : onCancel;

  const modalContent = (
    <div className="garage-modal-overlay" onClick={dismiss}>
      <div className="garage-modal garage-confirm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="garage-modal-header">
          <div className="garage-modal-title-row">
            <div className="garage-modal-icon">
              {getIcon()}
            </div>
            <h2>{title}</h2>
          </div>
          <button
            className="garage-modal-close"
            onClick={onCancel}
            disabled={isLoading}
            aria-label="Close"
          >
            <X size={20} />
          </button>
        </div>

        <div className="garage-modal-body">
          <p className="garage-confirm-message">{message}</p>
        </div>

        <div className="garage-modal-actions">
          <button
            type="button"
            className="garage-btn garage-btn-secondary"
            onClick={onCancel}
            disabled={isLoading}
          >
            {cancelText}
          </button>
          <button
            type="button"
            className={getConfirmButtonClass()}
            onClick={onConfirm}
            disabled={isLoading}
            aria-busy={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 size={16} className="garage-confirm-spinner" />
                {loadingText || confirmText}
              </>
            ) : (
              confirmText
            )}
          </button>
        </div>
      </div>
    </div>
  );

  // Render modal using React Portal at document.body level
  return ReactDOM.createPortal(modalContent, document.body);
};

export default ConfirmModal;
